import React from 'react';
import { API_BASE, toUrl } from '../lib/config';

type AboutRow = {
  id: string;
  page: string;
  aboutTitle?: string | null;
  logoUrl?: string | null;
  valuesKicker?: string | null;
  valuesTitleGreen?: string | null;
  valuesTitleMain?: string | null;
  valuesText?: string | null;
  isActive: boolean;
  updatedAt: string;
};

type TextItem = {
  id: string;
  aboutId: string;
  orderIndex: number;
  text: string;
  isActive: boolean;
  updatedAt: string;
};

type MediaOpt = { id?: string; name: string; url: string };

const PageAboutIntroYonetim: React.FC = () => {
  const PAGE = 'hakkimizda';

  // About list
  const [items, setItems] = React.useState<AboutRow[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [q, setQ] = React.useState('');

  // Edit about
  const [editingId, setEditingId] = React.useState('');
  const [eAboutTitle, setEAboutTitle] = React.useState('');
  const [eLogoUrl, setELogoUrl] = React.useState('');
  const [eKicker, setEKicker] = React.useState('');
  const [eTitleGreen, setETitleGreen] = React.useState('');
  const [eTitleMain, setETitleMain] = React.useState('');
  const [eText, setEText] = React.useState('');
  const [eActive, setEActive] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  // Media for logo selection
  const [mediaOpts, setMediaOpts] = React.useState<MediaOpt[]>([]);
  const resolveMedia = (u?: string | null) => {
    if (!u) return '';
    const uuidRe = /^[0-9a-fA-F-]{36}$/;
    const path = uuidRe.test(u) ? `/api/media/${u}` : u;
    return toUrl(path);
  };
  const normalizeMediaForSave = (u?: string | null) => {
    if (!u) return null;
    const m = u.match(/([0-9a-fA-F-]{36})$/);
    return m ? `/api/media/${m[1]}` : u;
  };
  const ImgWithFallback: React.FC<{ src?: string | null; alt: string; height?: number }> = ({ src, alt, height = 40 }) => {
    const baseIn = src || '';
    const m = baseIn.match(/([0-9a-fA-F-]{36})$/);
    const raw = m ? `/api/media/${m[1]}` : baseIn;
    const pref = toUrl(raw);
    const [currentSrc, setCurrentSrc] = React.useState(pref);
    return (
      <img
        src={currentSrc}
        alt={alt}
        style={{ height, objectFit: 'contain', borderRadius: 6, border: '1px solid #e5e7eb' }}
        crossOrigin="anonymous"
        onError={(e) => {
          const el = e.currentTarget as HTMLImageElement;
          const cur = el.getAttribute('src') || '';
          const next = cur === pref ? raw : pref;
          if (next !== cur) { el.src = next; setCurrentSrc(next); }
        }}
      />
    );
  };

  // Items for selected about
  const [selectedAbout, setSelectedAbout] = React.useState<AboutRow | null>(null);
  const [textItems, setTextItems] = React.useState<TextItem[]>([]);
  const [loadingTexts, setLoadingTexts] = React.useState(false);

  // CRUD for text items
  const [createOpen, setCreateOpen] = React.useState(false);
  const [cOrder, setCOrder] = React.useState<number>(0);
  const [cText, setCText] = React.useState('');
  const [cActive, setCActive] = React.useState(true);
  const [creating, setCreating] = React.useState(false);

  const [editingTextId, setEditingTextId] = React.useState('');
  const [eTOrder, setETOrder] = React.useState<number>(0);
  const [eTText, setETText] = React.useState('');
  const [eTActive, setETActive] = React.useState(true);
  const [savingText, setSavingText] = React.useState(false);
  const [delTextId, setDelTextId] = React.useState('');
  const [delTextLoading, setDelTextLoading] = React.useState(false);

  const fetchAbouts = React.useCallback(async () => {
    try {
      setLoading(true); setError('');
      const res = await fetch(toUrl(`/api/page-about-intro/admin/${PAGE}`));
      if (!res.ok) throw new Error('Kayıtlar alınamadı');
      const data = await res.json();
      setItems(data || []);
    } catch (e: any) { setError(e.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setLoading(false); }
  }, []);

  const fetchMedia = React.useCallback(async () => {
    try {
      const res = await fetch(toUrl('/api/media/admin'));
      if (!res.ok) return;
      const data = await res.json();
      setMediaOpts((data || []).map((m: any) => ({ id: m.id, name: m.name || m.originalName || m.id || m.url, url: m.url || `/api/media/${m.id}` })));
    } catch { /* ignore */ }
  }, []);

  const fetchTextItems = React.useCallback(async (aboutId: string) => {
    try {
      setLoadingTexts(true); setError('');
      const res = await fetch(toUrl(`/api/page-about-intro/admin/${aboutId}/text-items`));
      if (!res.ok) throw new Error('Metin maddeleri alınamadı');
      const data = await res.json();
      setTextItems(data || []);
    } catch (e: any) { setError(e.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setLoadingTexts(false); }
  }, []);

  React.useEffect(() => { fetchAbouts(); fetchMedia(); }, [fetchAbouts, fetchMedia]);

  const filtered = React.useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return items;
    return items.filter(i => (
      (i.aboutTitle || '').toLowerCase().includes(t) ||
      (i.valuesKicker || '').toLowerCase().includes(t) ||
      (i.valuesTitleGreen || '').toLowerCase().includes(t) ||
      (i.valuesTitleMain || '').toLowerCase().includes(t) ||
      (i.valuesText || '').toLowerCase().includes(t)
    ));
  }, [items, q]);

  const openEdit = (row: AboutRow) => {
    setEditingId(row.id);
    setEAboutTitle(row.aboutTitle || '');
    setELogoUrl(row.logoUrl || '');
    setEKicker(row.valuesKicker || '');
    setETitleGreen(row.valuesTitleGreen || '');
    setETitleMain(row.valuesTitleMain || '');
    setEText(row.valuesText || '');
    setEActive(!!row.isActive);
  };
  const cancelEdit = () => { setEditingId(''); setEAboutTitle(''); setELogoUrl(''); setEKicker(''); setETitleGreen(''); setETitleMain(''); setEText(''); setEActive(true); };
  const onSave = async (id: string) => {
    try {
      setSaving(true); setError('');
      const body: any = {
        aboutTitle: eAboutTitle || null,
        logoUrl: normalizeMediaForSave(eLogoUrl),
        valuesKicker: eKicker || null,
        valuesTitleGreen: eTitleGreen || null,
        valuesTitleMain: eTitleMain || null,
        valuesText: eText || null,
        isActive: !!eActive,
      };
      const res = await fetch(toUrl(`/api/page-about-intro/admin/${id}`), { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Güncelleme başarısız');
      cancelEdit(); await fetchAbouts();
    } catch (e: any) { setError(e.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setSaving(false); }
  };

  const openTexts = (row: AboutRow) => { setSelectedAbout(row); setCreateOpen(false); fetchTextItems(row.id); };
  const resetCreateText = () => { setCOrder(0); setCText(''); setCActive(true); };
  const onCreateText = async (e: React.FormEvent) => {
    e.preventDefault(); if (!selectedAbout) return;
    if (!cText.trim()) { setError('Lütfen metin girin'); return; }
    try {
      setCreating(true); setError('');
      const body: any = { aboutId: selectedAbout.id, orderIndex: cOrder || 0, text: cText.trim(), isActive: !!cActive };
      const res = await fetch(toUrl(`/api/page-about-intro/admin/${selectedAbout.id}/text-items`), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Oluşturma başarısız');
      resetCreateText(); setCreateOpen(false); await fetchTextItems(selectedAbout.id);
    } catch (e: any) { setError(e.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setCreating(false); }
  };

  const openEditText = (it: TextItem) => { setEditingTextId(it.id); setETOrder(it.orderIndex || 0); setETText(it.text || ''); setETActive(!!it.isActive); };
  const cancelEditText = () => { setEditingTextId(''); setETOrder(0); setETText(''); setETActive(true); };
  const onSaveText = async (id: string) => {
    if (!eTText.trim()) { setError('Lütfen metin girin'); return; }
    try {
      setSavingText(true); setError('');
      const body: any = { orderIndex: eTOrder || 0, text: eTText.trim(), isActive: !!eTActive };
      const res = await fetch(toUrl(`/api/page-about-intro/admin/text-items/${id}`), { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Güncelleme başarısız');
      cancelEditText(); if (selectedAbout) await fetchTextItems(selectedAbout.id);
    } catch (e: any) { setError(e.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setSavingText(false); }
  };
  const onDeleteText = (id: string) => setDelTextId(id);
  const confirmDeleteText = async () => {
    if (!delTextId) return;
    try {
      setDelTextLoading(true);
      const res = await fetch(toUrl(`/api/page-about-intro/admin/text-items/${delTextId}`), { method: 'DELETE' });
      if (!res.ok) throw new Error('Silme başarısız');
      setDelTextId(''); if (selectedAbout) await fetchTextItems(selectedAbout.id);
    } catch (e: any) { setError(e.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setDelTextLoading(false); }
  };

  return (
    <div className="admin-page" style={{ padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Hakkımızda Giriş (About Intro) – {PAGE}</h2>
        <div style={{ fontSize: 12, color: '#6b7280' }}>Toplam: {items.length}</div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div style={{ padding: 12, display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ara: başlık, değerler" className="input" style={{ width: 300 }} />
            {q && <button onClick={() => setQ('')} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>Temizle</button>}
          </div>
          <button onClick={fetchAbouts} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>Yenile</button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Başlık</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Logo URL</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Values Kicker</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Values Title Green</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Values Title Main</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Values Text</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Aktif</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Güncellenme</th>
                <th style={{ textAlign: 'right', padding: '12px 16px' }}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={9} style={{ padding: 16, color: '#6b7280' }}>Yükleniyor...</td></tr>)}
              {!loading && filtered.length === 0 && (<tr><td colSpan={9} style={{ padding: 16, color: '#6b7280' }}>Kayıt bulunamadı.</td></tr>)}
              {filtered.map(row => (
                <tr key={row.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px 16px', minWidth: 220 }}>
                    {editingId === row.id ? (
                      <textarea className="input" rows={2} value={eAboutTitle} onChange={(e) => setEAboutTitle(e.target.value)} />
                    ) : (row.aboutTitle || '-')}
                  </td>
                  <td style={{ padding: '12px 16px', minWidth: 240 }}>
                    {editingId === row.id ? (
                      <div style={{ display: 'grid', gap: 6 }}>
                        <select className="input" value={eLogoUrl || ''} onChange={(e) => setELogoUrl(e.target.value)}>
                          <option value="">Seçiniz</option>
                          {mediaOpts.map(m => (
                            <option key={(m.id || m.url)} value={(m.id || m.url)}>{m.name}</option>
                          ))}
                        </select>
                        {eLogoUrl && (<ImgWithFallback src={eLogoUrl} alt="logo" height={40} />)}
                      </div>
                    ) : (
                      row.logoUrl ? (
                        <ImgWithFallback src={row.logoUrl} alt="logo" height={70} />
                      ) : '-'
                    )}
                  </td>
                  <td style={{ padding: '12px 16px', minWidth: 200 }}>
                    {editingId === row.id ? (
                      <input className="input" value={eKicker} onChange={(e) => setEKicker(e.target.value)} />
                    ) : (row.valuesKicker || '-')}
                  </td>
                  <td style={{ padding: '12px 16px', minWidth: 200 }}>
                    {editingId === row.id ? (
                      <input className="input" value={eTitleGreen} onChange={(e) => setETitleGreen(e.target.value)} />
                    ) : (row.valuesTitleGreen || '-')}
                  </td>
                  <td style={{ padding: '12px 16px', minWidth: 200 }}>
                    {editingId === row.id ? (
                      <input className="input" value={eTitleMain} onChange={(e) => setETitleMain(e.target.value)} />
                    ) : (row.valuesTitleMain || '-')}
                  </td>
                  <td style={{ padding: '12px 16px', minWidth: 240 }}>
                    {editingId === row.id ? (
                      <textarea className="input" rows={3} value={eText} onChange={(e) => setEText(e.target.value)} />
                    ) : (row.valuesText || '-')}
                  </td>
                  <td style={{ padding: '12px 16px', width: 140 }}>
                    {editingId === row.id ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input id={`act-${row.id}`} type="checkbox" checked={eActive} onChange={(e) => setEActive(e.target.checked)} />
                        <label htmlFor={`act-${row.id}`}>Aktif</label>
                      </div>
                    ) : (
                      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 12, background: row.isActive ? '#ecfdf5' : '#fef2f2', color: row.isActive ? '#065f46' : '#991b1b', border: `1px solid ${row.isActive ? '#a7f3d0' : '#fecaca'}` }}>{row.isActive ? 'Aktif' : 'Pasif'}</span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px' }}>{row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '-'}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    {editingId === row.id ? (
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <button onClick={() => onSave(row.id)} disabled={saving} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #16a34a', background: '#16a34a', color: '#fff', cursor: 'pointer', opacity: saving ? 0.8 : 1 }}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</button>
                        <button onClick={cancelEdit} disabled={saving} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>İptal</button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button onClick={() => openTexts(row)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #0ea5e9', background: '#fff', color: '#0ea5e9', cursor: 'pointer' }}>İçerikler</button>
                        <button onClick={() => openEdit(row)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #2563eb', background: '#fff', color: '#2563eb', cursor: 'pointer' }}>Düzenle</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Text items panel */}
      {selectedAbout && (
        <div className="card" style={{ padding: 16, marginTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <h4 style={{ margin: 0 }}>Seçili About: {selectedAbout.aboutTitle || selectedAbout.id}</h4>
            <button type="button" onClick={() => setSelectedAbout(null)} aria-label="Kapat" style={{ background: 'transparent', border: 0, fontSize: 18, lineHeight: 1, cursor: 'pointer', color: '#6b7280' }}>×</button>
          </div>

          <div className="card" style={{ padding: 16, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ fontWeight: 600 }}>Yeni Metin</div>
              <button onClick={() => { resetCreateText(); setCreateOpen(s => !s); }} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>{createOpen ? 'Kapat' : 'Yeni Ekle'}</button>
            </div>
            {createOpen && (
              <form onSubmit={onCreateText} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                <div style={{ display: 'grid', gap: 6, minWidth: 0 }}>
                  <label className="form-label">Sıra</label>
                  <input className="input" type="number" value={cOrder} onChange={(e) => setCOrder(parseInt(e.target.value || '0', 10))} />
                </div>
                <div style={{ display: 'grid', gap: 6, gridColumn: '1 / -1', minWidth: 0 }}>
                  <label className="form-label">Metin</label>
                  <textarea className="input" rows={3} value={cText} onChange={(e) => setCText(e.target.value)} required />
                </div>
                <div style={{ display: 'grid', gap: 6, minWidth: 0 }}>
                  <label className="form-label">Aktif</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input id="cActive" type="checkbox" checked={cActive} onChange={(e) => setCActive(e.target.checked)} />
                    <label htmlFor="cActive">Aktif</label>
                  </div>
                </div>
                <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
                  <button type="submit" disabled={creating} style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid #16a34a', background: '#16a34a', color: '#fff', cursor: 'pointer', opacity: creating ? 0.8 : 1 }}>{creating ? 'Ekleniyor...' : 'Ekle'}</button>
                </div>
              </form>
            )}
          </div>

          <div className="card" style={{ padding: 0 }}>
            <div style={{ padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontWeight: 600 }}>Metin Maddeleri</div>
              <button onClick={() => selectedAbout && fetchTextItems(selectedAbout.id)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>Yenile</button>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f3f4f6' }}>
                    <th style={{ textAlign: 'left', padding: '12px 16px' }}>Sıra</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px' }}>Metin</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px' }}>Aktif</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px' }}>Güncellenme</th>
                    <th style={{ textAlign: 'right', padding: '12px 16px' }}>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingTexts && (<tr><td colSpan={5} style={{ padding: 16, color: '#6b7280' }}>Yükleniyor...</td></tr>)}
                  {!loadingTexts && textItems.length === 0 && (<tr><td colSpan={5} style={{ padding: 16, color: '#6b7280' }}>Kayıt bulunamadı.</td></tr>)}
                  {textItems.map(it => (
                    <tr key={it.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '12px 16px', width: 120 }}>
                        {editingTextId === it.id ? (
                          <input className="input" type="number" value={eTOrder} onChange={(e) => setETOrder(parseInt(e.target.value || '0', 10))} />
                        ) : it.orderIndex}
                      </td>
                      <td style={{ padding: '12px 16px', minWidth: 320 }}>
                        {editingTextId === it.id ? (
                          <textarea style={{ width: '100%' }} className="input" rows={3} value={eTText} onChange={(e) => setETText(e.target.value)} />
                        ) : it.text}
                      </td>
                      <td style={{ padding: '12px 16px', width: 140 }}>
                        {editingTextId === it.id ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input id={`tAct-${it.id}`} type="checkbox" checked={eTActive} onChange={(e) => setETActive(e.target.checked)} />
                            <label htmlFor={`tAct-${it.id}`}>Aktif</label>
                          </div>
                        ) : (
                          <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 12, background: it.isActive ? '#ecfdf5' : '#fef2f2', color: it.isActive ? '#065f46' : '#991b1b', border: `1px solid ${it.isActive ? '#a7f3d0' : '#fecaca'}` }}>{it.isActive ? 'Aktif' : 'Pasif'}</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px' }}>{it.updatedAt ? new Date(it.updatedAt).toLocaleString() : '-'}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                        {editingTextId === it.id ? (
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                            <button onClick={() => onSaveText(it.id)} disabled={savingText} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #16a34a', background: '#16a34a', color: '#fff', cursor: 'pointer', opacity: savingText ? 0.8 : 1 }}>{savingText ? 'Kaydediliyor...' : 'Kaydet'}</button>
                            <button onClick={cancelEditText} disabled={savingText} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>İptal</button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button onClick={() => openEditText(it)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #2563eb', background: '#fff', color: '#2563eb', cursor: 'pointer' }}>Düzenle</button>
                            <button onClick={() => onDeleteText(it.id)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #dc2626', background: '#dc2626', color: '#fff', cursor: 'pointer' }}>Sil</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Delete text confirm */}
      {delTextId && (
        <div role="dialog" aria-modal="true" aria-labelledby="delTextTitle" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050 }} onClick={(e) => { if (e.target === e.currentTarget) setDelTextId(''); }} onKeyDown={(e) => { if (e.key === 'Escape') setDelTextId(''); }} tabIndex={-1}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(480px,92vw)', background: '#fff', borderRadius: 12, boxShadow: '0 10px 25px rgba(0,0,0,0.15)', overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h5 id="delTextTitle" style={{ margin: 0, fontSize: 18 }}>Metin Silme Onayı</h5>
              <button type="button" aria-label="Close" onClick={() => setDelTextId('')} style={{ background: 'transparent', border: 0, fontSize: 18, lineHeight: 1, cursor: 'pointer', color: '#6b7280' }}>×</button>
            </div>
            <div style={{ padding: 16 }}>Bu metni silmek istediğinize emin misiniz? Bu işlem geri alınamaz.</div>
            <div style={{ padding: 12, borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setDelTextId('')} disabled={delTextLoading} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>İptal</button>
              <button type="button" onClick={confirmDeleteText} disabled={delTextLoading} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #dc2626', background: '#dc2626', color: '#fff', cursor: 'pointer', opacity: delTextLoading ? 0.8 : 1 }}>{delTextLoading ? 'Siliniyor...' : 'Evet, Sil'}</button>
            </div>
          </div>
        </div>
      )}

      {error && <div style={{ marginTop: 8, color: '#b00020' }}>{error}</div>}
    </div>
  );
};

export default PageAboutIntroYonetim;
